import { useNavigate, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { FaEnvelope, FaLock, FaUser, FaPhone, FaCheck, FaTimes } from "react-icons/fa";
import WebsiteNavbar from "../components/WebsiteNavbar";
import {
  register,
  verifyRegistrationOtp,
  resendRegistrationOtp,
  clearTokens,
} from "../services/authService";
import { checkPasswordComplexity, PASSWORD_REQUIREMENTS } from "../utils/passwordValidator";
import "../style/auth.css";
import loginImage from "../images/loginImage.png";

export default function SignUpPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Sign up details, 2: Verify Email OTP
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [resendTimer, setResendTimer] = useState(0);

  const [fields, setFields] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    password: "",
    confirm_password: "",
  });

  const pwValidation = checkPasswordComplexity(fields.password);
  const passwordsMatch = fields.password.length > 0 && fields.password === fields.confirm_password;

  useEffect(() => {
    setError("");
  }, [step]);

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  function handleChange(e) {
    setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSignUpSubmit(e) {
    e.preventDefault();
    setError("");

    if (!pwValidation.isValid) {
      setError(pwValidation.error);
      return;
    }
    if (fields.password !== fields.confirm_password) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    const result = await register({
      first_name: fields.first_name.trim(),
      last_name: fields.last_name.trim(),
      email: fields.email.trim(),
      mobile: fields.mobile.trim(),
      password: fields.password,
      account_type: "customer",
    });
    setLoading(false);

    if (result.success) {
      setStep(2);
      setResendTimer(60);
      setOtpDigits(["", "", "", "", "", ""]);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } else {
      setError(result.error || "Registration failed. Please try again.");
    }
  }

  const handleOtpChange = (index, value) => {
    if (value && !/^[0-9]$/.test(value)) return;
    const updated = [...otpDigits];
    updated[index] = value;
    setOtpDigits(updated);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const updated = pasted.split("");
    while (updated.length < 6) updated.push("");
    setOtpDigits(updated);
    otpRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    setError("");
    const otp = otpDigits.join("");
    if (otp.length !== 6) {
      setError("Please enter all 6 digits of the verification code.");
      return;
    }

    setLoading(true);
    const result = await verifyRegistrationOtp(fields.email.trim(), otp);
    setLoading(false);

    if (result.success) {
      clearTokens();
      navigate("/loginpage", {
        state: {
          registered: true,
          email: fields.email.trim(),
        },
      });
    } else {
      setError(result.error || "Verification failed. Please check the code and try again.");
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setError("");
    setOtpDigits(["", "", "", "", "", ""]);
    setResending(true);
    const result = await resendRegistrationOtp(fields.email.trim());
    setResending(false);
    if (result.success) {
      setResendTimer(60);
    } else {
      setError(result.error || "Failed to resend code.");
    }
  };

  return (
    <>
      <WebsiteNavbar />

      <div className="auth-page">
        <div className="auth-card">

          {/* LEFT SIDE */}
          <div className="auth-left">
            {step === 1 ? (
              <>
                <h1 className="auth-title">
                  Create <span className="auth-accent">Account</span>
                </h1>

                <p className="auth-subtitle">
                  Sign up free and get your 7th bin hire on us.
                </p>

                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleSignUpSubmit}>
                  {/* Name row */}
                  <div className="signup-name-row">
                    <div className="auth-field">
                      <FaUser className="auth-field-icon" />
                      <input
                        name="first_name"
                        type="text"
                        placeholder="First name"
                        value={fields.first_name}
                        onChange={handleChange}
                        className="auth-input"
                        autoComplete="given-name"
                        required
                      />
                    </div>
                    <div className="auth-field">
                      <FaUser className="auth-field-icon" />
                      <input
                        name="last_name"
                        type="text"
                        placeholder="Last name"
                        value={fields.last_name}
                        onChange={handleChange}
                        className="auth-input"
                        autoComplete="family-name"
                        required
                      />
                    </div>
                  </div>

                  <div className="auth-field">
                    <FaEnvelope className="auth-field-icon" />
                    <input
                      name="email"
                      type="email"
                      placeholder="Email address"
                      value={fields.email}
                      onChange={handleChange}
                      className="auth-input"
                      autoComplete="email"
                      required
                    />
                  </div>

                  <div className="auth-field">
                    <FaPhone className="auth-field-icon" />
                    <input
                      name="mobile"
                      type="tel"
                      placeholder="Mobile number"
                      value={fields.mobile}
                      onChange={handleChange}
                      className="auth-input"
                      autoComplete="tel"
                      required
                    />
                  </div>

                  <div className="auth-field">
                    <FaLock className="auth-field-icon" />
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Password (min. 8 characters)"
                      value={fields.password}
                      onChange={handleChange}
                      className="auth-input"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="input-icon-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>

                  <div className="auth-field">
                    <FaLock className="auth-field-icon" />
                    <input
                      name="confirm_password"
                      type={showConfirm ? "text" : "password"}
                      placeholder="Confirm password"
                      value={fields.confirm_password}
                      onChange={handleChange}
                      className="auth-input"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="input-icon-btn"
                      onClick={() => setShowConfirm(!showConfirm)}
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                    >
                      {showConfirm ? "🙈" : "👁️"}
                    </button>
                  </div>

                  {fields.password.length > 0 && (
                    <div className="auth-pw-checklist">
                      {PASSWORD_REQUIREMENTS.map((r) => (
                        <div
                          key={r.key}
                          className={`auth-pw-req ${pwValidation.checks[r.key] ? "met" : "unmet"}`}
                        >
                          {pwValidation.checks[r.key] ? <FaCheck size={11} /> : <FaTimes size={11} />}
                          <span>{r.label}</span>
                        </div>
                      ))}
                      {fields.confirm_password.length > 0 && (
                        <div
                          className={`auth-pw-req ${passwordsMatch ? "met" : "unmet"}`}
                        >
                          {passwordsMatch ? <FaCheck size={11} /> : <FaTimes size={11} />}
                          <span>Passwords match</span>
                        </div>
                      )}
                    </div>
                  )}

                  <button type="submit" className="auth-submit-btn" disabled={loading}>
                    {loading ? "Sending verification code..." : "Sign Up"}
                  </button>
                </form>

                <p className="auth-footer-text">
                  Already have an account? <Link to="/loginpage">Sign in</Link>
                </p>
              </>
            ) : (
              <>
                <h1 className="auth-title">
                  Verify <span className="auth-accent">Email</span>
                </h1>

                <p className="auth-subtitle">
                  We sent a 6-digit verification code to <strong>{fields.email}</strong>
                </p>

                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleVerifyOtp}>
                  <div className="fp-otp-row" onPaste={handleOtpPaste}>
                    {otpDigits.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (otpRefs.current[i] = el)}
                        className="fp-otp-input"
                        type="text"
                        maxLength={1}
                        inputMode="numeric"
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        autoFocus={i === 0}
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    className="auth-submit-btn"
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Verify & Sign Up"}
                  </button>

                  <p className="auth-footer-text">
                    Didn't receive code?{" "}
                    <button
                      type="button"
                      className="fp-link"
                      onClick={handleResend}
                      disabled={resending || resendTimer > 0}
                    >
                      {resending
                        ? "Sending..."
                        : resendTimer > 0
                        ? `Resend in ${resendTimer}s`
                        : "Resend code"}
                    </button>
                  </p>

                  <p className="auth-footer-text" style={{ marginTop: 12 }}>
                    Entered wrong email?{" "}
                    <button
                      type="button"
                      className="fp-link"
                      onClick={() => setStep(1)}
                    >
                      Edit your details.
                    </button>
                  </p>
                </form>
              </>
            )}
          </div>

          {/* RIGHT SIDE */}
          <div className="auth-right">
            <div className="auth-illustration">
              <img src={loginImage} alt="Illustration" className="auth-image" />
            </div>
          </div>

        </div>
      </div>
    </>
  );
}